import { datadogRum } from '@datadog/browser-rum';
import { useQueryCache } from '@pinia/colada';
import { Router } from 'vue-router';

import { useAgentChatStore } from '@tallo/investor/agent-chat';
import { useAvailabilityStore } from '@tallo/investor/availability';
import { useCompanyStore } from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import { AppRouteNames } from '@tallo/investor/navigation';
import { useUserStore } from '@tallo/user';

class AppInitService {
  private router: Router | null = null;

  setRouter(router: Router): void {
    this.router = router;
  }

  async initApp(): Promise<void> {
    await this.loadAndTrackUser();
  }

  async loadAndTrackUser(): Promise<void> {
    const investorStore = useInvestorStore();

    try {
      await investorStore.load();

      if (investorStore.investor?.user) {
        const user = investorStore.investor.user;
        datadogRum.setUser({
          id: user.id,
          email: user.email,
          name: user.name,
        });
      }

      this.checkOnboardingStatus();
    } catch (error) {
      console.error('Failed to load investor data:', error);
    }
  }

  private checkOnboardingStatus(): void {
    if (!this.router) return;

    const investorStore = useInvestorStore();
    const currentRoute = this.router.currentRoute.value;

    if (this.isRouteAllowed(currentRoute.name as string)) {
      return;
    }

    if (investorStore.needsInitialOnboarding) {
      this.router.push({ name: AppRouteNames.INVESTOR_ONBOARDING });
    }
  }

  private isRouteAllowed(routeName: string): boolean {
    if (!routeName) return false;

    const publicRoutes = [
      AppRouteNames.SIGN_IN,
      AppRouteNames.SIGN_UP,
      AppRouteNames.SIGN_UP_CONFIRM_EMAIL,
      AppRouteNames.RESET_PASSWORD,
      AppRouteNames.RESET_PASSWORD_CONFIRM,
      AppRouteNames.MISSING_PASSWORD_SIGN_IN,
      AppRouteNames.ONE_TIME_CODE_SIGN_IN,
      AppRouteNames.TEAM_INVITE,
    ];

    const isOnboardingRoute = routeName.toLowerCase().includes('onboarding');

    return publicRoutes.includes(routeName as AppRouteNames) || isOnboardingRoute;
  }

  reset() {
    const queryCache = useQueryCache();
    const userStore = useUserStore();
    const availabilityStore = useAvailabilityStore();
    const companyStore = useCompanyStore();
    const investorStore = useInvestorStore();

    userStore.$reset();
    availabilityStore.$reset();
    companyStore.$reset();
    investorStore.$reset();
    useAgentChatStore().reset();

    queryCache.getEntries().forEach((entry) => {
      queryCache.invalidate(entry);
      queryCache.remove(entry);
    });
  }
}

export default new AppInitService();
